// Die eine Vektorisierung ohne Konto -- der Teil, der ohne React auskommt.
//
// Zwei Oberflächen lösen denselben Gast-Weg aus: die Seite /ausprobieren
// (components/GuestTrace.tsx, ein React-Chunk) und die Upload-Fläche im Hero
// der Startseite (landing/guestUpload.ts, Vanilla-TS im Einstiegsbündel --
// die Startseite lädt vertraglich genau ein Bündel, e2e/code-splitting.spec.ts).
// Was beide tun, steht hier genau einmal: Datei prüfen, an
// `POST /api/v1/guest/traces` schicken, Dokument und Bild in die IndexedDB
// legen (editor/guestResult.ts), den Fehlercode des Servers in einen
// Fehler-Schlüssel übersetzen. Die Sätze dazu kennt der Aufrufer -- der eine
// aus de.ts, der andere aus einem data-Attribut, das der Server aus den
// Locales gerendert hat.
//
// Bewusst ohne Import aus `../i18n`: das Einstiegsbündel soll die
// Übersetzungstabelle des Editors nicht mitladen.
//
// Und bewusst ohne statischen Import aus `../editor/guestResult`: der Speicher
// wird erst geholt, wenn wirklich eine Datei kommt (`import()` unten). Statisch
// hinge am Einstiegsbündel eine Kette Modul -> Modul, deren Glieder sich mit
// *verschiedenen* nachgeladenen Chunks teilen (guestResult mit /ausprobieren,
// indexedDb mit dem Editor und dem Dashboard) -- und genau diese Form legt
// rolldown je nach Modulreihenfolge als eigenes Stück neben das Einstiegsbündel,
// womit die Startseite zwei Skripte lüde. Ein `import()` beim ersten Upload
// ist auf der Startseite ohnehin die richtige Reihenfolge: bis dahin braucht
// niemand eine IndexedDB.

import type { GuestResultStorage } from '../editor/guestResult'
import type { EditorDocument } from '../editor/document'
import { ApiError, apiSendForm, isNetworkError } from './api'

// Dieselbe Zahl wie `Api::V1::TraceInput::MAX_UPLOAD_BYTES`. Hier nur, um vor
// dem Hochladen einen Satz sagen zu können statt nach zwanzig Megabyte.
export const GUEST_MAX_UPLOAD_BYTES = 20 * 1024 * 1024

export const GUEST_ACCEPT = 'image/png,image/jpeg,image/webp,image/avif,image/heic,image/heif,application/pdf'

export const GUEST_TRACE_PATH = '/api/v1/guest/traces'

export type GuestTraceResponse = {
  document: EditorDocument
  layers_count: number
}

/** Was der Server tut -- als Schnittstelle, damit vitest ohne fetch auskommt. */
export type GuestTraceApi = (form: FormData) => Promise<GuestTraceResponse>

/**
 * Warum es nichts wurde. Die Schlüssel sind zugleich die Schlüssel der
 * Sätze (de.ts `guest.errors.*`, Locales `home.upload.messages.*`).
 */
export type GuestTraceFailureCode = 'tooLarge' | 'unsupported' | 'limit' | 'busy' | 'timeout' | 'offline' | 'failed'

export type GuestTraceFailure = {
  ok: false
  code: GuestTraceFailureCode
  /**
   * Ob dieselbe Datei gleich noch einmal eine Chance hat. Ein voller Topf,
   * eine Deadline, ein Netzfehler: ja. Ein verbrauchtes Kontingent, eine zu
   * große oder unlesbare Datei: nein -- da hilft nur ein Konto oder ein
   * anderes Bild.
   */
  retryable: boolean
  /** Aus `Retry-After` des Servers, wenn er eine Zahl genannt hat (guest_busy). */
  retryAfterSeconds?: number
}

export type GuestTraceOutcome = { ok: true; key: string } | GuestTraceFailure

function failure(code: GuestTraceFailureCode, retryable: boolean, retryAfterSeconds?: number): GuestTraceFailure {
  return retryAfterSeconds === undefined ? { ok: false, code, retryable } : { ok: false, code, retryable, retryAfterSeconds }
}

/** Der Fehler eines Aufrufs als Schlüssel. */
export function guestTraceFailure(error: unknown): GuestTraceFailure {
  if (isNetworkError(error)) return failure('offline', true)

  if (error instanceof ApiError) {
    const body = (error.body ?? null) as { error?: string; retry_after?: number } | null
    const code = body?.error
    if (code === 'guest_limit_reached') return failure('limit', false)
    // 429 ist rack-attack (`gast-trace/ip`): dieselbe Antwort wie ein voller
    // Topf -- gerade viel los, gleich noch einmal.
    if (code === 'guest_busy' || error.status === 429) {
      return failure('busy', true, typeof body?.retry_after === 'number' ? body.retry_after : undefined)
    }
    if (code === 'guest_timeout') return failure('timeout', true)
    if (code === 'unsupported_content_type') return failure('unsupported', false)
    if (code === 'file_too_large') return failure('tooLarge', false)
  }

  return failure('failed', true)
}

/** Wie das Ergebnis heißt: der Dateiname ohne Endung, gedeckelt. */
export function guestResultName(file: File): string {
  return file.name.replace(/\.[^./\\]+$/, '').slice(0, 120) || 'Motiv'
}

export function sendGuestTrace(form: FormData): Promise<GuestTraceResponse> {
  return apiSendForm<GuestTraceResponse>(GUEST_TRACE_PATH, form)
}

type TraceOptions = {
  send?: GuestTraceApi
  /** `undefined` nimmt die IndexedDB der Seite, `null` gar keinen Speicher. */
  storage?: GuestResultStorage | null
  now?: () => number
}

/**
 * Die Datei vektorisieren lassen und das Ergebnis im Browser ablegen.
 *
 * Das Bild geht mit in den Speicher, nicht nur das Dokument: ohne Quellbild
 * wäre das später übernommene Projekt eines, das sich nie wieder neu
 * vektorisieren lässt. Zurück kommt der Schlüssel, unter dem beides liegt --
 * oder der Grund, warum nicht.
 */
export async function traceAsGuest(file: File, options: TraceOptions = {}): Promise<GuestTraceOutcome> {
  if (file.size > GUEST_MAX_UPLOAD_BYTES) return failure('tooLarge', false)

  const send = options.send ?? sendGuestTrace
  const now = options.now ?? Date.now

  const form = new FormData()
  form.append('image', file)

  try {
    const { defaultGuestResultStorage, newGuestResultKey } = await import('../editor/guestResult')
    const storage = options.storage === undefined ? defaultGuestResultStorage() : options.storage
    const response = await send(form)
    const key = newGuestResultKey()

    await storage?.put({
      key,
      name: guestResultName(file),
      document: response.document,
      image: file,
      imageName: file.name,
      createdAt: now(),
    })

    return { ok: true, key }
  } catch (error) {
    return guestTraceFailure(error)
  }
}
